import { Component, OnInit, Input, forwardRef, OnChanges, SimpleChanges, Output, EventEmitter, OnDestroy, AfterViewInit } from '@angular/core';
import {
  FormControl,
  Validators,
  FormGroup,
  AbstractControl,
  ValidationErrors,
  NG_VALUE_ACCESSOR,
  NG_VALIDATORS,
  ControlValueAccessor,
} from '@angular/forms';
import { Store } from '@ngrx/store';
import { AppState } from '@app/state/app.state';
import { SupplyDetailsDto } from '../../models/supply-details';
import { Observable, of, Subscription } from 'rxjs';
import { CommodityType } from '@app/shared/enums/commodity-type';
import { UtilityHubUtilityInformation, UtilityInformation, UtilityZoneInformation } from '@app/shared/models/utility-information';
import { distinctUntilChanged, first, switchMap, tap } from 'rxjs/operators';
import { UtilityDetails } from '@app/shared/models/customer-details';
import { UtilityHubIdentity } from '@auth/models/utility-hub-identity';
import { SupplierUtilityModel } from '@app/shared/models/supplier-utility-model';
import { SetUtility, SetUtilityUOM, SetUtilityDgsShortName, SetUtilityPowwrPricingName, SetUtilitySupplierPricingName, SetUtilityRisk360Name, LoadedUtilityInformation, SetAddress, ClearUtilityInformation, ClearUtility } from '@utility-information/state/actions/utility-information.actions';
import { utilityUOMSelector, utilitySelector, utilityInformationSelector, zonesSelector, weatherZonesSelector, isoSelector, commodityTypeSelector, stateSelector, supplierUtilityInformationSelector, utilityUomByUtilityShortNameSelector } from '@utility-information/state/selectors/utility-information.selectors';
import { isReadOnlySelector, loggedInUtilityHubIdentitySelector } from '@app/state/app.selectors';
import { UtilityInformationService } from '@app/shared/services/utility-information.service';
import { QuoteRequestType } from '@app/shared/models/QuoteRequestType';
import { UtilityDetailsService } from '@app/shared/services/utility-details.service';

@Component({
  selector: 'app-utility-details-presentation',
  templateUrl: './utility-details-presentation.component.html',
  styleUrls: ['./utility-details-presentation.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => UtilityDetailsPresentationComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => UtilityDetailsPresentationComponent),
      multi: true,
    },
  ],
})
export class UtilityDetailsPresentationComponent implements OnInit, OnChanges, ControlValueAccessor, OnDestroy {
  public onTouched: () => void = () => { };
  
  register$: Subscription;
  utilityShort$: Observable<string[]>;
  iso$: Observable<string>;
  zone$: Subscription;
  weatherZones$: Observable<string[]>;
  selectedZone: string;

  @Input() quoteRequestType: QuoteRequestType;
  @Input() utilityShort: string;
  @Input() iso: string;
  @Input() zones: string[];
  @Input() weatherZones: string[];
  @Input() utilities: string[];
  @Input() showValidation: boolean;
  @Input() showControls: {
    showBundledRateCheckbox: boolean;
    showIso: boolean;
    showZone: boolean;
    loadingUtilities: boolean;
    emitEventOnValueChange: boolean;
  };

  @Output() utilitySelected: EventEmitter<string> = new EventEmitter();
  @Output() zoneSelected: EventEmitter<string> = new EventEmitter();
  @Output() weatherZoneSelected: EventEmitter<string> = new EventEmitter();
  @Output() utilityDetailsChanged: EventEmitter<UtilityDetails> = new EventEmitter();
  @Output() utilityUomSelected: EventEmitter<string> = new EventEmitter();
  @Output() utilityZipCodes: EventEmitter<any[]> = new EventEmitter();

  frmSupplyDetails: FormGroup;
  selectedUtility: string;
  //onChange: any;
  onChange: (value?: any) => void = () => {};
  utilityInformation$: Observable<UtilityInformation[]>;
  zones$: Observable<string[]>;
  commodityType$: Observable<string>;
  quoteRequestId: string;
  CommodityType = CommodityType;
  usState$: Observable<string>;
  uom$: Subscription;
  utilityUOM$: Observable<string>;
  utility$: Observable<string>;
  supplierUtilityInformation$: Observable<SupplierUtilityModel[]>;
  uhIdentity$: Subscription;
  userIdentity: UtilityHubIdentity;
  displayError: boolean = false;
  currentCommodity: string;
  currentState: string;
  zipCodes: any[];
  isReadOnly$: Subscription;
  isReadOnly: boolean;

  get utilityCtrl() {
    return this.frmSupplyDetails?.get('utilityShort');
  }
  get zoneCtrl() {
    return this.frmSupplyDetails?.get('zone');
  }
  get weatherZoneCtrl() {
    return this.frmSupplyDetails?.get('weatherZone');
  }
  get isoCtrl() {
    return this.frmSupplyDetails?.get('iso');
  }
  get uomCtrl() {
    return this.frmSupplyDetails?.get('uom');
  }
  get commodityCtrl() {
    return this.frmSupplyDetails?.get('commodity');
  }
  get dgsShortNameCtrl() {
    return this.frmSupplyDetails?.get('dgsShortName');
  }
  get powwrPricingNameCtrl() {
    return this.frmSupplyDetails?.get('powwrPricingName');
  }
  get supplierPricingNameCtrl() {
    return this.frmSupplyDetails?.get('supplierPricingName');
  }
  get risk360NameCtrl() {
    return this.frmSupplyDetails?.get('risk360Name');
  }

  constructor(
    private store: Store<AppState>,
    private utilityInformationService: UtilityInformationService,    
    private utilityDetailsService: UtilityDetailsService,
  ) {
    this.utilityUOM$ = this.store.select(utilityUOMSelector);
    this.utility$ = this.store.select(utilitySelector);
  }

  ngOnDestroy(): void {
    this.zone$?.unsubscribe();
    this.uom$?.unsubscribe();
    this.isReadOnly$?.unsubscribe();
  }

  ngOnInit(): void {
    this.utilityDetailsService.utilityShortNameChanged.subscribe((utility) => {
      if (utility)
        this.selectUtility(utility);
    });

    this.utilityInformation$ = this.store.select(utilityInformationSelector).pipe(
      distinctUntilChanged(
        (prev, curr) => JSON.stringify(prev.map((x) => x.utilityShort)) === JSON.stringify(curr.map((y) => y.utilityShort))
      ),
      switchMap((utilityInformation) => {
        
        if (utilityInformation.length === 1) {
          this.setSingleUtility(utilityInformation[0].utilityShort);
          this.setSingleIso(utilityInformation[0].iso);
          this.setSingleUtilityUOM(utilityInformation[0].uom);
          this.utilityUomSelected.emit(utilityInformation[0].uom);
          this.setSingleUtilityShortNameCollection(utilityInformation[0]);
          this.selectUtilityemit();
          return of(utilityInformation);
        } 
        else if (utilityInformation.length === 0) {
          this.clearFormFields();
        } else {
          this.setSingleUtility("");
        }

        if (!this.isReadOnly) {
          this.utilityCtrl.enable();
          this.dgsShortNameCtrl.enable();
          this.powwrPricingNameCtrl.enable();
          this.supplierPricingNameCtrl.enable();
          this.risk360NameCtrl.enable();
        }
       
        return of(utilityInformation);
      })
    );

    this.zones$ = this.store.select(zonesSelector).pipe(
      distinctUntilChanged((prev, curr) => JSON.stringify(prev.map((x) => x)) === JSON.stringify(curr.map((y) => y))),
      switchMap((zones) => {
        
        if (this.userIdentity.currentOrganisation.externalId.toLowerCase() === 'cleansky-001-s'){
          zones = zones.filter(x=> !x.toLowerCase().includes('_hub'));
        }
        if (zones.length === 1) {
          this.setSingleZone(zones[0]);
          this.selectUtilityemit();
          return of(zones);
        }

        if (!this.isReadOnly) {
          this.zoneCtrl.enable();
        }

        return of(zones);
      })
    );

    this.weatherZones$ = this.store.select(weatherZonesSelector).pipe(
      distinctUntilChanged((prev, curr) => JSON.stringify(prev.map((x) => x)) === JSON.stringify(curr.map((y) => y))),
      switchMap((weatherZones) => {
        
        if (weatherZones.length === 1) {
          this.setSingleWeatherZone(weatherZones[0]);
          this.selectUtilityemit();
          return of(weatherZones);
        }

        if (!this.isReadOnly) {
          this.weatherZoneCtrl.enable();
        }

        return of(weatherZones);
      })
    );
    this.iso$ = this.store.select(isoSelector).pipe(
      distinctUntilChanged(),
      switchMap((iso) => {
        this.setSingleIso(iso);
        return of(iso);
      })
    );
    this.commodityType$ = this.store.select(commodityTypeSelector).pipe(
      switchMap((c) => {
        this.resetSelectedUtility(c);
        this.currentCommodity = c;
        this.updateZoneValidator();
        return of(c);
      })
    );
    
    this.usState$ = this.store.select(stateSelector);
    this.frmSupplyDetails = new FormGroup({
      bundledRate: new FormControl({ value: '', disabled: false }),
      commodity: new FormControl({ value: CommodityType.Electricity, disabled: false }, Validators.required),
      iso: new FormControl({ value: '', disabled: false }),
      utilityShort: new FormControl({ value: '', disabled: false }, Validators.required),
      weatherZone: new FormControl({ value: '', disabled: false }),
      zone: new FormControl({ value: '', disabled: true }),
      uom: new FormControl({ value: '', disabled: true }),
      dgsShortName: new FormControl({ value: '', disabled: true }),
      powwrPricingName: new FormControl({ value: '', disabled: true }),
      supplierPricingName: new FormControl({ value: '', disabled: true }),
      risk360Name: new FormControl({ value: '', disabled: true }),
    });

    this.uhIdentity$ = this.store.select(loggedInUtilityHubIdentitySelector).subscribe((uhUser) => {
      this.userIdentity = uhUser;
    });

    this.isReadOnly$ = this.store.select(isReadOnlySelector).subscribe((isReadOnly) => {
      this.isReadOnly = isReadOnly;
      if (this.isReadOnly) {
        this.frmSupplyDetails.disable({ emitEvent: false });
      } 
    });  
  }

  clearFormFields() {
    this.utilityCtrl.setValue('');
    this.isoCtrl.setValue('');
    this.zoneCtrl.setValue('');
    this.weatherZoneCtrl.setValue('');
    this.uomCtrl.setValue('');
    this.dgsShortNameCtrl.setValue('');
    this.powwrPricingNameCtrl.setValue('');
    this.supplierPricingNameCtrl.setValue('');
    this.risk360NameCtrl.setValue('');
    this.utilityCtrl.markAsPristine();
    this.utilityCtrl.markAsUntouched();
    this.zoneCtrl.markAsPristine();
    this.zoneCtrl.markAsUntouched();
    this.store.dispatch(new SetAddress(this.quoteRequestId, undefined, undefined));
    this.store.dispatch(new ClearUtilityInformation(this.quoteRequestId));
    this.store.dispatch(new ClearUtility(this.quoteRequestId));
  }

  updateZoneValidator(){
    if (this.currentCommodity === CommodityType.Gas) {
    this.zoneCtrl.clearValidators();
    }
    else if (this.currentCommodity === CommodityType.Electricity){
      this.zoneCtrl.addValidators(Validators.required);
    }
  }

  updateWeatherZoneValidator(){
    if (this.currentState === 'TX' && this.currentCommodity === CommodityType.Electricity) {
      this.weatherZoneCtrl.addValidators(Validators.required);
    }
    else
    {
      this.weatherZoneCtrl.clearValidators();
      this.weatherZoneCtrl.setValue('');
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.frmSupplyDetails) {
      if (changes?.showControls) {
        this.showControls = changes?.showControls?.currentValue;
      }
      if (changes?.zones){
        if (this.zones != null && typeof this.zones != "undefined" && !this.zones.includes(this.zoneCtrl.value)) {
          this.zoneCtrl.setValue('');
        }
      }
      if (changes?.weatherZones){
        if (this.weatherZones != null && typeof this.weatherZones != "undefined" && !this.weatherZones.includes(this.weatherZoneCtrl.value)) {
          this.weatherZoneCtrl.setValue('');
        }
      }
      if (this.showValidation) {
        this.frmSupplyDetails.markAllAsTouched();
      }
    }  
  }

  setSingleIso(iso: string) {
    this.isoCtrl.setValue(iso);
    this.isoCtrl.disable();
  }

  utilityValidation(utility: string) {
    let supplierInformations: SupplierUtilityModel[];
    this.store
      .select(supplierUtilityInformationSelector)
      .pipe(first())
      .subscribe((resp) => {
        supplierInformations = resp;
      })
      .unsubscribe();
    let existInUH = supplierInformations.filter((x) => x.utility.shortName.toLocaleLowerCase() === utility.toLocaleLowerCase()).length > 0;
    if (!existInUH && utility !== '') {
      this.displayError = true;
      this.frmSupplyDetails.get('utilityShort').setErrors({ valid: false });
    } else {
      this.displayError = false;
      this.utilityCtrl.disable();
    }
  }
  
  
  mapUtilityZoneInformation(data: any[], utilityShort: string): UtilityZoneInformation[] {
    const zones = new Set<string>();
    const zoneInfoMap: Map<string, string> = new Map();

    data.forEach(item => {
        zones.add(item.zone);
        if (item.zone && item.weatherZone) {
            zoneInfoMap.set(item.zone, item.weatherZone);
        }
    });

    return Array.from(zones).map(zone => {
        return {
            utilityShort: utilityShort,
            loadZone: zone,
            weatherZone: zoneInfoMap.get(zone) || null,
            capacityZone: null, 
        };
    });
}

  mapUtilityShortNameCollection(
    utilitySelected: UtilityInformation,
    utilityInfo: UtilityHubUtilityInformation
  ): UtilityInformation {
    const {
      dgsShortName,
      powwrPricingName,
      supplierPricingName,
      risk360Name
    } = utilityInfo;
  
    return {
      ...utilitySelected,
      dgsShortName,
      powwrPricingName,
      supplierPricingName,
      risk360Name,
      uom: utilityInfo.uom,
    };
  }

  // control value changes
  selectUtility(utility: string) {
    
    this.utilityInformationService.getUtilityInformationByShortName(utility).subscribe(u => {
      
      this.zipCodes = u[0]?.zipCodes;
      let updatedUtilitySelected = this.mapUtilityShortNameCollection(this.getSingleUtilityFromList(utility)[0], u[0]);

      updatedUtilitySelected.utilityZoneInformation = this.mapUtilityZoneInformation(this.zipCodes, utility);

      this.utilityZipCodes.emit(this.zipCodes);
      this.store.dispatch(new SetUtilityUOM(this.quoteRequestId, updatedUtilitySelected.uom));
      this.store.dispatch(new LoadedUtilityInformation([updatedUtilitySelected]));

      this.setSingleUtilityShortNameCollection(updatedUtilitySelected);
      this.utilityValidation(utility);
      this.utilitySelected.emit(utility); // cascade form model update
      this.store.dispatch(new SetUtility(this.quoteRequestId, utility)); // update the UI state
      this.uom$ = this.store.select(utilityUomByUtilityShortNameSelector).subscribe((x) => {
        this.selectUtilityUOM(x);
      });
      
    });

  }

  selectUtilityUOM(utilityUOM: string) {
    this.store.dispatch(new SetUtilityUOM(this.quoteRequestId, utilityUOM)); // update the UI state
    this.uomCtrl.setValue(utilityUOM);
    this.formDataChange();
    this.utilityUomSelected.emit(utilityUOM);
    this.selectUtilityemit();
  }

  selectUtilityemit() {
    const formData: any = this.frmSupplyDetails.getRawValue();
    this.utilityDetailsChanged.emit(formData);
  }

  setSingleUtility(utility: string) {
    this.utilityCtrl.setValue(utility);
    this.formDataChange();
    this.utilityValidation(utility);
    this.store.dispatch(new SetUtility(this.quoteRequestId, utility));
    this.utilitySelected.emit(utility);
    this.updateZoneValidator();
    this.usState$.subscribe((s)=> {
      this.currentState = s;
      this.updateWeatherZoneValidator();
    });
  }

  setSingleUtilityUOM(utilityUOM: string) {
    this.store.dispatch(new SetUtilityUOM(this.quoteRequestId, utilityUOM));
    this.uomCtrl.setValue(utilityUOM);
    this.formDataChange();
  }

  setSingleUtilityShortNameCollection(utilityInformation: UtilityInformation) {
    this.store.dispatch(new SetUtilityDgsShortName(this.quoteRequestId, utilityInformation?.dgsShortName));
    this.dgsShortNameCtrl.setValue(utilityInformation?.dgsShortName);

    this.store.dispatch(new SetUtilityPowwrPricingName(this.quoteRequestId, utilityInformation?.powwrPricingName));
    this.powwrPricingNameCtrl.setValue(utilityInformation?.powwrPricingName);

    this.store.dispatch(new SetUtilitySupplierPricingName(this.quoteRequestId, utilityInformation?.supplierPricingName));
    this.supplierPricingNameCtrl.setValue(utilityInformation?.supplierPricingName);

    this.store.dispatch(new SetUtilityRisk360Name(this.quoteRequestId, utilityInformation?.risk360Name));
    this.risk360NameCtrl.setValue(utilityInformation?.risk360Name);

    this.formDataChange();
  }

  selectZone() {
    const zone = this.selectedZone;
    this.formDataChange();
    this.zoneSelected.emit(zone);
    this.selectUtilityemit();
  }

  formDataChange() {
    this.frmSupplyDetails.updateValueAndValidity();
    this.onChange(this.frmSupplyDetails.getRawValue());
  }

  getSingleUtilityFromList(utilityShort: string) {
    let utility: UtilityInformation[];
    this.utilityInformation$.subscribe((o) => (utility = o.filter((p) => p.utilityShort == utilityShort)));
    return utility;
  }

  setSingleZone(zone: string) {
    this.zoneCtrl.setValue(zone);
    this.formDataChange();
    this.zoneSelected.emit(zone);
    this.zoneCtrl.disable();
  }

  selectWeatherZone(weatherZone: string) {
    this.formDataChange();
    this.weatherZoneSelected.emit(weatherZone);
    this.selectUtilityemit();
  }

  setSingleWeatherZone(weatherZone: string) {
    this.weatherZoneCtrl.setValue(weatherZone);
    this.formDataChange();
    this.weatherZoneCtrl.disable();
    this.weatherZoneSelected.emit(weatherZone);
  }

  validate(control: AbstractControl): ValidationErrors {
    return this.frmSupplyDetails.valid ? null : { invalidForm: { valid: false, message: 'Supply Details Invalid' } };
  }

  registerOnValidatorChange?(fn: () => void): void { }

  writeValue(val: SupplyDetailsDto): void {
    if (val) this.frmSupplyDetails.patchValue(val, { emitEvent: false });
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  resetSelectedUtility(c: any) {
    this.utilityCtrl.setValue('');
    this.commodityCtrl.setValue(c);
  }
}
